import axios from "axios"
import { useQuery } from "@tanstack/react-query"
import { GET_ABCI_INFO } from "src/constants/queryKeys"
import { useMemo } from "react"

const ABCI_INFO_ENDPOINT = process.env.REACT_APP_ABCI_INFO_ENDPOINT

const MAINTENANCE_BLOCK_HEIGHT = Number(
  process.env.REACT_APP_MAINTENANCE_BLOCK_HEIGHT,
)

// Upgrade block height is 6542800
// We want to show the warning between min and max block height (-200 blocks and +200 blocks)
const MIN_BLOCK_HEIGHT = MAINTENANCE_BLOCK_HEIGHT - 200

const MAX_BLOCK_HEIGHT = MAINTENANCE_BLOCK_HEIGHT + 200

// Check upgrade status every 20 seconds
const INTERVAL_MS = 1000 * 20

export interface AbciInfoResponseBody {
  jsonrpc: string
  id: number
  result: {
    response: {
      data: string
      version: string
      /* eslint-disable camelcase */
      last_block_height: string
      /* eslint-disable camelcase */
      last_block_app_hash: string
    }
  }
}

function shouldShowWarning(lastBlockHeight: string): boolean {
  try {
    const parsedBlockHeight = parseInt(lastBlockHeight, 10)

    if (
      parsedBlockHeight > MIN_BLOCK_HEIGHT &&
      parsedBlockHeight < MAX_BLOCK_HEIGHT
    ) {
      return true
    }

    return false
  } catch (error) {
    // Failed to parse block height, let's be safe and show the warning
    return true
  }
}

function useShowCronosUpgradeWarning() {
  const {
    data: abciInfoResponse,
    isLoading,
    isError,
  } = useQuery(
    [GET_ABCI_INFO],
    () => axios.get<AbciInfoResponseBody>(ABCI_INFO_ENDPOINT || ""),
    {
      refetchInterval: INTERVAL_MS,
    },
  )
  const showWarning = useMemo(() => {
    if (abciInfoResponse?.data?.result?.response?.last_block_height) {
      return shouldShowWarning(
        abciInfoResponse.data.result.response.last_block_height,
      )
    }
    if (isError) {
      return true
    }
    return !isLoading
  }, [abciInfoResponse, isLoading, isError])

  return {
    showWarning,
  }
}

export default useShowCronosUpgradeWarning
